/**
 * Integrytas Group - Portal Framework (PFW)
 * Page: PFW.PORTALS
 *
 * Setup interface for portals: branding/flags, curated views (CMS pages / app
 * screens), the per-portal data entity whitelist (read/write), and programmatic
 * API keys. The gateway enforces the whitelist when a portal/API-key header is
 * present; views/keys are admin-managed.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { registerUdcHost } from '../../lib/udc_hosts';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'PFW.PORTALS';
registerUdcHost(PAGE_CODE, 'pfw_portal', 'Portals');

const PORTAL_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text', label: 'Code', required: true, col_span: 1 },
  { key: 'name',          type: 'text', label: 'Name', col_span: 1 },
];
const PORTAL_CORE_LAYOUT = toUldCoreLayout(PORTAL_FIELD_DEFS);

const PORTAL_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',  header: 'Code',     defaultWidth: 220 },
  { key: 'name',           header: 'Name',     defaultWidth: 240 },
  { key: 'allow_external', header: 'External', defaultWidth: 110 },
  { key: 'is_active',      header: 'Active',   defaultWidth: 90  },
];

interface PortalRow {
  pfw_portal_id: number; internal_code: string; name: string;
  branding: unknown; allow_external: 'Y' | 'N'; is_active: 'Y' | 'N'; description: string | null;
}
interface ViewRow { pfw_portal_view_id: number; view_kind: 'CMS' | 'APP'; view_code: string; required_role_id: number | null; sort_order: number; is_active: 'Y' | 'N' }
interface EntityRow { pfw_portal_entity_id: number; entity_name: string; can_read: 'Y' | 'N'; can_write: 'Y' | 'N' }
interface KeyRow { pfw_api_key_id: number; internal_code: string; key_prefix: string; scopes: string; is_active: 'Y' | 'N' }

export interface PfwPortalManagerPageProps { session: AuthSession }

export function PfwPortalManagerPage({ session }: PfwPortalManagerPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <PfwPortalManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function PfwPortalManagerPageInner({ session }: PfwPortalManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<PortalRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PortalRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the list's logical columns.
  const [facets, setFacets] = useState<{ external: string[]; active: string[] }>({ external: [], active: ['Y'] });

  const udcGrid = useUdcGrid('pfw_portal', PAGE_CODE, { pkColumn: 'pfw_portal_id', rows: rows as unknown as Record<string, unknown>[] });

  // §17.B13: expand/contract related-children. The list view loads only portal
  // rows; the child collections (views, entities, keys) are fetched inside the
  // editor modal, so nothing is available to populate here — control is inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — expose portal views/entities/keys at list level to populate.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('pfw_portal')
        .select('pfw_portal_id, internal_code, name, branding, allow_external, is_active, description').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as PortalRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded rows (§17.B5). External / Active
  // are Y/N flags, so the option set is fixed.
  const facetOptions = useMemo(() => ({
    external: [{ value: 'Y', label: t('common.yes', 'Yes') }, { value: 'N', label: t('common.no', 'No') }],
    active:   [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
  }), [t]);

  const facetsActive = facets.external.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ external: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const matchesFacets = (r: PortalRow) =>
      (facets.external.length === 0 || facets.external.includes(r.allow_external)) &&
      (facets.active.length   === 0 || facets.active.includes(r.is_active));
    const base = (!q ? rows : rows.filter(r => r.internal_code.toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q)))
      .filter(matchesFacets);
    return base.filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
  }, [rows, filter, facets, udcGrid]);

  const handleDelete = async (row: PortalRow) => {
    const { error: e } = await routedDelete(session, 'pfw_portal', 'pfw_portal_id', row.pfw_portal_id);
    if (e) { setError(e.message); return; }
    setDeleteTarget(null);
    void reload();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('pfw_portals.title', 'Portals')}
          description={t('pfw_portals.list.description', 'Third-party portals — branding, curated views, data whitelist and API keys.')}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('pfw_portals.refresh', 'Refresh')}
              </button>
              {canAdd && (<button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('pfw_portals.new_portal', 'New Portal')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('pfw_portals.stat.total', 'Portals') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('pfw_portals.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('pfw_portals.filter_placeholder', 'Filter portals…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('pfw_portals.col.allow_external', 'External')} options={facetOptions.external} selected={facets.external} onChange={v => setFacets(f => ({ ...f, external: v }))} />
            <FacetFilter label={t('pfw_portals.col.is_active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('pfw_portals.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...PORTAL_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('pfw_portals.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('pfw_portals.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.pfw_portal_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.pfw_portal_id)} hasChildren={false} onToggle={() => detail.toggle(r.pfw_portal_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code'  && <span className="font-mono">{r.internal_code}</span>}
                        {col.key === 'name'           && r.name}
                        {col.key === 'allow_external' && <YesNoBadge value={r.allow_external} />}
                        {col.key === 'is_active'      && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)    && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('pfw_portals.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('pfw_portals.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.pfw_portal_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('pfw_portals.empty', 'No portals yet')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('pfw_portals.add_new', '+ New Portal')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <PortalModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={t('pfw_portals.delete_confirm.title', 'Delete Portal?')}
          message={t('pfw_portals.delete_confirm.message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function PortalModal({ session, row, onClose, onSaved }: { session: AuthSession; row: PortalRow | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const id = row?.pfw_portal_id;

  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    brandText: (() => {
      try {
        // Sort keys so the stable string never causes a false-dirty on open.
        const obj = row?.branding ?? { title: '', logo_url: '' };
        const sorted = Object.fromEntries(Object.entries(obj as Record<string, unknown>).sort());
        return JSON.stringify(sorted, null, 2);
      } catch { return '{}'; }
    })(),
    allowExt: row ? row.allow_external === 'Y' : true,
    active: row ? row.is_active === 'Y' : false,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('pfw_portal', PAGE_CODE, { pkColumn: 'pfw_portal_id', pk: row?.pfw_portal_id });
  const portalCoreLayout = useMemo(() => [...PORTAL_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const portalFieldDefs = useMemo(() => [...PORTAL_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('PFW.PORTAL.MODAL', portalCoreLayout);
  const [views, setViews] = useState<ViewRow[]>([]);
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [newKeyCode, setNewKeyCode] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('READ');
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const loadChildren = async () => {
    if (!id) return;
    const [v, e, k] = await Promise.all([
      apiDb.from('pfw_portal_view').select('pfw_portal_view_id, view_kind, view_code, required_role_id, sort_order, is_active').eq('pfw_portal_id', id).order('sort_order'),
      apiDb.from('pfw_portal_entity').select('pfw_portal_entity_id, entity_name, can_read, can_write').eq('pfw_portal_id', id).order('entity_name'),
      apiDb.from('pfw_api_key').select('pfw_api_key_id, internal_code, key_prefix, scopes, is_active').eq('pfw_portal_id', id).order('internal_code'),
    ]);
    setViews((v.data ?? []) as ViewRow[]); setEntities((e.data ?? []) as EntityRow[]); setKeys((k.data ?? []) as KeyRow[]);
  };

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    void loadChildren();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validateCode = (val: string) => {
    if (!val) { setInternalCodeError(null); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(val)) {
      setInternalCodeError(t('pfw_portals.code_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setInternalCodeError(null);
    }
  };

  const savePortal = async () => {
    setBusy(true); setError(null);
    try {
      let branding: unknown; try { branding = JSON.parse(form.brandText); } catch { throw new Error(t('pfw_portals.error.branding_json', 'Branding must be valid JSON.')); }
      if (isCreate && !form.code.trim()) throw new Error(t('pfw_portals.error.code_required', 'Code is required.'));
      if (internalCodeError) throw new Error(internalCodeError);
      const payload = { name: form.name.trim(), branding, allow_external: form.allowExt ? 'Y' : 'N', is_active: form.active ? 'Y' : 'N' };
      if (isCreate) { const { error: e } = await routedInsert(session, 'pfw_portal', { internal_code: form.code.trim().toUpperCase(), ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'pfw_portal', 'pfw_portal_id', id, payload); if (e) throw e; }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addView = async () => { if (!id) return; await routedInsert(session, 'pfw_portal_view', { pfw_portal_id: id, view_kind: 'CMS', view_code: '', sort_order: (views.length ? views[views.length - 1].sort_order : 0) + 1 }); await loadChildren(); };
  const saveView = async (v: ViewRow) => { await routedUpdate(session, 'pfw_portal_view', 'pfw_portal_view_id', v.pfw_portal_view_id, { view_kind: v.view_kind, view_code: v.view_code, required_role_id: v.required_role_id, sort_order: v.sort_order, is_active: v.is_active }); };
  const delView = async (vid: number) => { await routedDelete(session, 'pfw_portal_view', 'pfw_portal_view_id', vid); setViews(vs => vs.filter(x => x.pfw_portal_view_id !== vid)); };

  const addEntity = async () => { if (!id) return; await routedInsert(session, 'pfw_portal_entity', { pfw_portal_id: id, entity_name: '', can_read: 'Y', can_write: 'N' }); await loadChildren(); };
  const saveEntity = async (en: EntityRow) => { await routedUpdate(session, 'pfw_portal_entity', 'pfw_portal_entity_id', en.pfw_portal_entity_id, { entity_name: en.entity_name, can_read: en.can_read, can_write: en.can_write }); };
  const delEntity = async (eid: number) => { await routedDelete(session, 'pfw_portal_entity', 'pfw_portal_entity_id', eid); setEntities(es => es.filter(x => x.pfw_portal_entity_id !== eid)); };

  const issueKey = async () => {
    if (!row || !newKeyCode.trim()) return;
    setError(null);
    const { data, error: e } = await apiDb.rpc('pfw_issue_api_key', { p_portal_code: row.internal_code, p_code: newKeyCode.trim().toUpperCase(), p_scopes: newKeyScope });
    if (e) { setError(e.message); return; }
    const res = data as { ok: boolean; api_key?: string; error_code?: string };
    if (!res?.ok) { setError(res?.error_code ?? 'PFW.KEY_FAILED'); return; }
    setIssuedKey(res.api_key ?? null); setNewKeyCode(''); await loadChildren();
  };
  const revokeKey = async (kid: number) => { await routedUpdate(session, 'pfw_api_key', 'pfw_api_key_id', kid, { is_active: 'N' }); await loadChildren(); };

  return (
    <>
      <Modal
        title={isCreate ? t('pfw_portals.modal.create_title', 'Create Portal') : t('pfw_portals.modal.edit_title', 'Edit Portal — {{code}}', { code: row?.internal_code ?? '' })}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('pfw_portals.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={portalFieldDefs}
            layout={layout}
            columns={2}
            pageCode="PFW.PORTAL.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'internal_code') return (
                <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('pfw_portals.field.code', 'Code')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pfw_portals.field.code', 'Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(f2 => ({ ...f2, code: e.target.value.toUpperCase() }))}
                         onBlur={e => validateCode(e.target.value)}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('pfw_portals.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>}
                </div>
                </PersonalizedField>
              );
              if (f.key === 'name') return (
                <PersonalizedField componentKey="modal" fieldKey="name" label={t('pfw_portals.field.name', 'Name')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pfw_portals.field.name', 'Name')}</label>
                  <input type="text" value={form.name} onChange={e => setForm(f2 => ({ ...f2, name: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></div>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>
        <PersonalizedField componentKey="modal" fieldKey="branding" label={t('pfw_portals.field.branding', 'Branding (JSON)')}>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pfw_portals.field.branding', 'Branding (JSON)')}</label>
          <textarea rows={3} value={form.brandText} onChange={e => setForm(f => ({ ...f, brandText: e.target.value }))} className="mb-3 w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs" />
        </PersonalizedField>
        <div className="mb-3 flex items-center gap-4">
          <PersonalizedField componentKey="modal" fieldKey="allow_external" label={t('pfw_portals.field.allow_external', 'Allow external')}>
            <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.allowExt} onChange={e => setForm(f => ({ ...f, allowExt: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('pfw_portals.field.allow_external', 'Allow external')}</label>
          </PersonalizedField>
          <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('pfw_portals.field.active', 'Active')}>
            <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('pfw_portals.field.active', 'Active')}</label>
          </PersonalizedField>
          <button onClick={() => void savePortal()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('pfw_portals.saving', 'Saving…') : t('pfw_portals.save_portal', 'Save Portal')}</button>
        </div>

        {!isCreate && (
          <>
            <Section title={t('pfw_portals.section.curated_views', 'Curated Views')} onAdd={() => void addView()}>
              {views.map((v, i) => (
                <div key={v.pfw_portal_view_id} className="mb-1 flex flex-wrap items-center gap-1">
                  <select value={v.view_kind} onChange={e => setViews(vs => vs.map((x, j) => j === i ? { ...x, view_kind: e.target.value as 'CMS' | 'APP' } : x))} onBlur={() => void saveView(views[i])} className="h-7 rounded border border-csm-border px-1 text-csm-xs"><option value="CMS">CMS</option><option value="APP">APP</option></select>
                  <input value={v.view_code} onChange={e => setViews(vs => vs.map((x, j) => j === i ? { ...x, view_code: e.target.value } : x))} onBlur={() => void saveView(views[i])} placeholder={t('pfw_portals.placeholder.view_code', 'view code')} className="h-7 w-48 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <input type="number" value={v.sort_order} onChange={e => setViews(vs => vs.map((x, j) => j === i ? { ...x, sort_order: Number(e.target.value) } : x))} onBlur={() => void saveView(views[i])} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-xs" />
                  <button onClick={() => void delView(v.pfw_portal_view_id)} className="text-csm-danger text-csm-xs">{t('pfw_portals.delete_lc', 'delete')}</button>
                </div>
              ))}
            </Section>

            <Section title={t('pfw_portals.section.entity_whitelist', 'Data Entity Whitelist')} onAdd={() => void addEntity()}>
              {entities.map((en, i) => (
                <div key={en.pfw_portal_entity_id} className="mb-1 flex flex-wrap items-center gap-2">
                  <input value={en.entity_name} onChange={e => setEntities(es => es.map((x, j) => j === i ? { ...x, entity_name: e.target.value } : x))} onBlur={() => void saveEntity(entities[i])} placeholder={t('pfw_portals.placeholder.entity', 'entity (table)')} className="h-7 w-56 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                  <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={en.can_read === 'Y'} onChange={e => { setEntities(es => es.map((x, j) => j === i ? { ...x, can_read: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveEntity(entities[i])} /> {t('pfw_portals.read', 'read')}</label>
                  <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={en.can_write === 'Y'} onChange={e => { setEntities(es => es.map((x, j) => j === i ? { ...x, can_write: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveEntity(entities[i])} /> {t('pfw_portals.write', 'write')}</label>
                  <button onClick={() => void delEntity(en.pfw_portal_entity_id)} className="text-csm-danger text-csm-xs">{t('pfw_portals.delete_lc', 'delete')}</button>
                </div>
              ))}
            </Section>

            <Section title={t('pfw_portals.section.api_keys', 'API Keys')}>
              {issuedKey && (
                <div className="mb-2 rounded border border-csm-warning/40 bg-csm-warning/10 p-2 text-csm-xs">
                  {t('pfw_portals.key_once_notice', 'Copy this key now — it is shown only once:')}
                  <div className="mt-1 break-all font-mono text-csm-sm">{issuedKey}</div>
                </div>
              )}
              <div className="mb-2 flex flex-wrap items-center gap-1">
                <input value={newKeyCode} onChange={e => setNewKeyCode(e.target.value.toUpperCase())} placeholder="KEY_CODE" className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                <select value={newKeyScope} onChange={e => setNewKeyScope(e.target.value)} className="h-7 rounded border border-csm-border px-1 text-csm-xs"><option value="READ">READ</option><option value="WRITE">WRITE</option><option value="READ_WRITE">READ_WRITE</option></select>
                <button onClick={() => void issueKey()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('pfw_portals.issue_key', 'Issue Key')}</button>
              </div>
              {keys.map(k => (
                <div key={k.pfw_api_key_id} className="mb-1 flex items-center gap-2 text-csm-xs">
                  <span className="font-mono">{k.internal_code}</span>
                  <span className="font-mono text-csm-watermark">{k.key_prefix}…</span>
                  <span>{k.scopes}</span>
                  <span className={k.is_active === 'Y' ? 'text-primary' : 'text-csm-muted'}>{k.is_active === 'Y' ? t('pfw_portals.active_lc', 'active') : t('pfw_portals.revoked_lc', 'revoked')}</span>
                  {k.is_active === 'Y' && <button onClick={() => void revokeKey(k.pfw_api_key_id)} className="text-csm-danger">{t('pfw_portals.revoke', 'revoke')}</button>}
                </div>
              ))}
            </Section>
          </>
        )}
        {error && (
          <div className="mt-3">
            <ErrorBanner
              message={t('pfw_portals.save_failed', "Couldn't save the portal.")}
              detail={error}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('pfw_portals.create_hint', 'Save the portal first to configure views, whitelist, and keys.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('pfw_portals.discard.title', 'Discard changes?')}
          message={t('pfw_portals.discard.message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('pfw_portals.discard.confirm', 'Discard')}
          cancelLabel={t('pfw_portals.discard.cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(initialValues); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd?: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 border-t border-csm-border pt-2">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        {onAdd && <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('pfw_portals.add', '+ Add')}</button>}
      </div>
      {children}
    </div>
  );
}
